import {FC, useState, useCallback} from "react";
import {Box, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, TablePagination, Card, CardContent, Button, IconButton, CircularProgress, Breadcrumbs} from "@mui/material";
import {Link} from "react-router-dom";
import {AdminHeader} from "@/pages/ManagerView/AdminHeader";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {AddRecipientModal} from "./AddRecipientModal";
import {RemoveRecipientModal} from "./RemoveRecipientModal";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {useGetDistributionRecipients, useAddDistributionRecipients, useRemoveDistributionRecipient, type DistributionRecipient} from "@/app2/shared/data";

export const DistributionListPage: FC = () => {
	const {t} = useSSTranslation();
	const {successSnackbar, errorSnackbar} = useCustomSnackbar();
	const {data: recipientsData, isLoading} = useGetDistributionRecipients();
	const addMutation = useAddDistributionRecipients();
	const removeMutation = useRemoveDistributionRecipient();
	const [addModalOpen, setAddModalOpen] = useState(false);
	const [removeModalOpen, setRemoveModalOpen] = useState(false);
	const [selectedRecipient, setSelectedRecipient] = useState<DistributionRecipient | null>(null);
	const [page, setPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(25);

	const recipients = recipientsData?.data || [];
	const totalCount = recipientsData?.totalCount || 0;

	const existingRecipientUserIds = recipients.map(r => r.userId);

	const handleAddRecipients = useCallback((userIds: number[]) => {
		addMutation.mutate(userIds, {
			onSuccess: (data) => {
				setAddModalOpen(false);
				successSnackbar(data.added > 0 ? "Recipients added successfully" : data.message);
			},
			onError: (err: Error) => {
				errorSnackbar(err?.message || "Failed to add recipients");
			}
		});
	}, [addMutation, successSnackbar, errorSnackbar]);

	const handleRemoveRecipient = useCallback((recipientId: number) => {
		removeMutation.mutate(recipientId, {
			onSuccess: () => {
				setRemoveModalOpen(false);
				setSelectedRecipient(null);
				successSnackbar("Recipient removed successfully");
			},
			onError: (err: Error) => {
				errorSnackbar(err?.message || "Failed to remove recipient");
			}
		});
	}, [removeMutation, successSnackbar, errorSnackbar]);

	const handleOpenRemoveModal = (recipient: DistributionRecipient) => {
		setSelectedRecipient(recipient);
		setRemoveModalOpen(true);
	};

	const handleChangePage = (_event: unknown, newPage: number) => {
		setPage(newPage + 1);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(1);
	};

	// Paginate recipients
	const paginatedRecipients = recipients.slice(
		(page - 1) * rowsPerPage,
		page * rowsPerPage
	);

	return (
		<Box sx={{display: "flex", justifyContent: "center"}}>
			<Stack sx={{maxWidth: "1444px", width: "100%"}} gap={3}>
				<Breadcrumbs sx={{mb: -2}}>
					<Link to="/admin/scheduled-reports" style={{textDecoration: "none", color: "inherit"}}>
						<Typography variant="body2" sx={{color: "rgba(255,255,255,0.7)", "&:hover": {color: "primary.main"}}}>
							Scheduled Reports
						</Typography>
					</Link>
					<Typography variant="body2" sx={{color: "rgba(255,255,255,0.7)"}}>
						Distribution List
					</Typography>
				</Breadcrumbs>
				<AdminHeader
					title="Distribution List"
					subtitle="Manage audience and delivery for scheduled reports"
				/>

				{/* Audience List Section */}
				<Card sx={{borderRadius: "8px", backgroundColor: "background.paper", border: "1px solid #A1A1A026"}}>
					<CardContent sx={{p: 3}}>
						<Stack spacing={2}>
							<Stack direction="row" justifyContent="space-between" alignItems="center">
								<Box>
									<Typography variant="h6" sx={{fontWeight: 600, mb: 0.5}}>
										<span style={{marginRight: "8px"}}>1</span>
										Audience List
									</Typography>
									<Typography variant="body2" sx={{color: "rgba(255,255,255,0.7)", ml: 3.5}}>
										Manage who receives the executive report.
									</Typography>
								</Box>
								<Button
									variant="outlined"
									color="primary"
									onClick={() => setAddModalOpen(true)}
									startIcon={<PersonAddIcon />}
									sx={{
										borderColor: "primary.main",
										color: "primary.main",
										"&:hover": {
											borderColor: "primary.main",
											backgroundColor: "rgba(255, 158, 0, 0.1)"
										}
									}}
								>
									ADD RECIPIENT
								</Button>
							</Stack>

							{isLoading ? (
								<Box sx={{display: "flex", justifyContent: "center", p: 4}}>
									<CircularProgress />
								</Box>
							) : (
								<>
									<TableContainer component={Paper} sx={{backgroundColor: "background.paper"}}>
										<Table sx={{"& .MuiTableCell-root": {borderColor: "rgba(255,255,255,0.1)"}}}>
											<TableHead>
												<TableRow>
													<TableCell><strong>Name</strong></TableCell>
													<TableCell><strong>Email</strong></TableCell>
													<TableCell><strong>Job Title</strong></TableCell>
													<TableCell><strong>Division</strong></TableCell>
													<TableCell align="right"><strong>Actions</strong></TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{paginatedRecipients.length === 0 ? (
													<TableRow>
														<TableCell colSpan={5} align="center" sx={{py: 4, color: "text.secondary"}}>
															No recipients added yet. Click &quot;ADD RECIPIENT&quot; to get started.
														</TableCell>
													</TableRow>
												) : (
													paginatedRecipients.map((recipient) => (
														<TableRow key={recipient.id}>
															<TableCell>{recipient.name}</TableCell>
															<TableCell>{recipient.email}</TableCell>
															<TableCell>{recipient.jobTitle ?? "—"}</TableCell>
															<TableCell>{recipient.division ?? "—"}</TableCell>
															<TableCell align="right">
																<IconButton
																	onClick={() => handleOpenRemoveModal(recipient)}
																	sx={{
																		color: "error.main",
																		"&:hover": {
																			backgroundColor: "rgba(244, 67, 54, 0.1)"
																		}
																	}}
																>
																	<DeleteIcon />
																</IconButton>
															</TableCell>
														</TableRow>
													))
												)}
											</TableBody>
										</Table>
									</TableContainer>
									{totalCount > 0 && (
										<TablePagination
											component="div"
											count={totalCount}
											page={page - 1}
											onPageChange={handleChangePage}
											rowsPerPage={rowsPerPage}
											onRowsPerPageChange={handleChangeRowsPerPage}
											rowsPerPageOptions={[25, 50, 100, 200]}
										/>
									)}
								</>
							)}
						</Stack>
					</CardContent>
				</Card>

				{/* Delivery Schedule Section */}
				<Card sx={{borderRadius: "8px", backgroundColor: "background.paper", border: "1px solid #A1A1A026"}}>
					<CardContent sx={{p: 3}}>
						<Stack spacing={2}>
							<Box>
								<Typography variant="h6" sx={{fontWeight: 600, mb: 0.5}}>
									<span style={{marginRight: "8px"}}>2</span>
									Delivery Schedule
								</Typography>
								<Typography variant="body2" sx={{color: "rgba(255,255,255,0.7)", ml: 3.5}}>
									Emails will be sent once each Month-end Report is generated, typically on the first day of each month.
								</Typography>
							</Box>
						</Stack>
					</CardContent>
				</Card>
			</Stack>

			{/* Modals */}
			{
				addModalOpen && (
					<AddRecipientModal
						open={addModalOpen}
						onClose={() => setAddModalOpen(false)}
						onSave={handleAddRecipients}
						existingRecipientUserIds={existingRecipientUserIds}
					/>
				)
			}
			{selectedRecipient && (
				<RemoveRecipientModal
					open={removeModalOpen}
					onClose={() => {
						setRemoveModalOpen(false);
						setSelectedRecipient(null);
					}}
					onConfirm={() => handleRemoveRecipient(selectedRecipient.id)}
					recipientName={selectedRecipient.name}
				/>
			)}
		</Box>
	);
};